import React, { useState } from 'react'
import { observer } from 'mobx-react'
import { Button } from 'antd'
import { useAccessOwner } from './access-owner-provider'
import type { StaleSource } from './access-owner'

const BANNER_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    marginBottom: 16,
    borderRadius: 4,
    color: 'var(--aqe-color-text, inherit)',
    background:
        'rgba(var(--aqe-color-warning-r, 250), var(--aqe-color-warning-g, 173), var(--aqe-color-warning-b, 20), 0.15)',
    border: '1px solid var(--aqe-color-warning, #faad14)',
}

/**
 * A quiet notice that a background refresh failed and the page shows the last loaded data. It is a
 * status, not an alert: the page still works. The status region is always in the DOM and is filled
 * when a source goes stale, which is what makes the notice get announced.
 */
export type StaleBannerProps = {
    /**
     * Runs each listed source's own refresh again. Each refresh records its own outcome on the owner,
     * so the banner clears itself when they succeed and stays when one fails again.
     */
    onRetry: (sources: StaleSource[]) => Promise<unknown>
}

export const StaleBanner = observer(({ onRetry }: StaleBannerProps) => {
    const [retrying, setRetrying] = useState(false)
    const stale = useAccessOwner().stale

    const retry = async () => {
        if (!stale) {
            return
        }
        setRetrying(true)
        try {
            await onRetry(stale.map((entry) => entry.source))
        } finally {
            setRetrying(false)
        }
    }

    return (
        <div role="status" aria-live="polite" data-testid="stale-banner-region">
            {stale ? (
                <div data-testid="stale-banner" style={BANNER_STYLE}>
                    <span>Some information could not be refreshed. You are seeing the last loaded version.</span>
                    <Button size="small" loading={retrying} onClick={() => void retry()}>
                        Retry
                    </Button>
                </div>
            ) : null}
        </div>
    )
})
